import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Receipt } from "lucide-react";
import { formatCurrency, formatCurrencyWithTenant } from "@/lib/utils";
import { useTenantCurrency } from "@/hooks/use-tenant";
import { formatCurrencyWithTenantAndConversion, convertCurrency } from "@/lib/currency-conversion";

interface ClaimsMetrics {
  total: { count: number; amount: number; currency: string };
  active: { count: number; amount: number; currency: string };
  settled: { count: number; amount: number; currency: string };
}

interface ProjectClaimsSummaryProps {
  projectId: string;
  loading?: boolean;
}

export default function ProjectClaimsSummary({ projectId, loading = false }: ProjectClaimsSummaryProps) {
  const { currency: tenantCurrency } = useTenantCurrency();
  const { data: metrics, isLoading } = useQuery<ClaimsMetrics>({
    queryKey: [`/api/projects/${projectId}/claims/metrics`],
    enabled: !!projectId && projectId !== 'new',
  });

  const isLoadingState = loading || isLoading;
  const hasNoClaims = !isLoadingState && metrics && metrics.total.count === 0;

  // Don't render if no claims exist
  if (hasNoClaims) {
    return null;
  }

  return (
    <Card className="bg-white shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
          <Receipt className="w-4 h-4 mr-2 text-blue-600" />
          Claims Summary
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoadingState ? (
          <div className="space-y-2">
            <div className="bg-muted animate-pulse rounded-md h-8 w-16" />
            <div className="bg-muted animate-pulse rounded-md h-4 w-32" />
            <div className="bg-muted animate-pulse rounded-md h-4 w-48" />
          </div>
        ) : metrics ? (
          <>
            <div className="text-2xl font-bold">{metrics.total.count}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrencyWithTenantAndConversion(metrics.total.amount, metrics.total.currency, tenantCurrency, {
                showSymbol: true,
                showOriginal: false,
                showConversionRate: false
              })} total amount
            </p>
            <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
              <span>
                Active: {metrics.active.count} ({formatCurrencyWithTenantAndConversion(metrics.active.amount, metrics.active.currency, tenantCurrency, {
                  showSymbol: true,
                  showOriginal: false,
                  showConversionRate: false
                })})
              </span>
              <span>
                Settled: {metrics.settled.count} ({formatCurrencyWithTenantAndConversion(metrics.settled.amount, metrics.settled.currency, tenantCurrency, {
                  showSymbol: true,
                  showOriginal: false,
                  showConversionRate: false
                })})
              </span>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}